/**
 * UI state store — active tab, sidebar collapse, toast queue, confirm modal.
 *
 * Active-tab persistence matches the monolith's `saveLastActiveTab()`:
 * the chosen tab is round-tripped through `sessionStorage` so a reload
 * keeps the user where they were within the session (but not across
 * browser restarts).
 */

import { create } from 'zustand';
import { uid } from '../utils/dom.js';
import { applyTheme, effectiveTheme } from '../utils/theme.js';

const ACTIVE_TAB_STORAGE_KEY = 'omni:lastActiveTab';
const SIDEBAR_STORAGE_KEY = 'omni:sidebarCollapsed';
const DEFAULT_TAB = 'dashboard';

/** Valid tab ids — keep in sync with the monolith's switchTab() switch. */
const VALID_TABS = new Set([
  'dashboard',
  'lookup',
  'admin',
  'shares',
  'user-management-v2',
  'printer-management',
  'ad-sync',
  'policy-finder',
  'reporting',
  'shared-mailboxes',
  'group-management',
  'asset-management',
  'security-dashboard',
  'tools',
  'network-dashboard',
  'sharepoint-management',
  'cost-analysis',
  'automations',
  'claude-usage',
  'compliance',
  'backups',
  'apps'
]);

/**
 * Is this a tab we can actually navigate to? Callers that build a tab id from
 * data (e.g. a help section) must check first — setActiveTab only warns and
 * does nothing for an unknown id, which would leave a dead button on screen.
 * @param {string} tabId
 */
export function isValidTab(tabId) {
  return VALID_TABS.has(tabId);
}

function readActiveTab() {
  try {
    const stored = sessionStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
    if (stored && VALID_TABS.has(stored)) return stored;
  } catch {
    /* ignore (private mode etc.) */
  }
  return DEFAULT_TAB;
}

function readSidebarCollapsed() {
  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * @typedef {'success' | 'error' | 'warning' | 'info'} ToastType
 *
 * @typedef {Object} ToastEntry
 * @property {string} id
 * @property {ToastType} type
 * @property {string} title
 * @property {string} [message]
 * @property {number} duration
 */

/**
 * @typedef {'warning' | 'danger' | 'success' | 'info'} ConfirmType
 *
 * @typedef {Object} ConfirmState
 * @property {string} title
 * @property {string} message               HTML allowed (matches monolith behaviour)
 * @property {string} confirmText
 * @property {ConfirmType} type
 * @property {(() => void) | null} onConfirm
 */

/**
 * @typedef {Object} UiState
 * @property {string} activeTab
 * @property {boolean} sidebarCollapsed
 * @property {boolean} sidebarMobileOpen
 * @property {ToastEntry[]} toasts
 * @property {ConfirmState | null} confirm
 * @property {boolean} globalSearchOpen
 * @property {string} globalSearchInitialQuery
 *
 * @property {(tabId: string) => void} setActiveTab
 * @property {() => void} toggleSidebarCollapsed
 * @property {(value?: boolean) => void} setSidebarMobileOpen
 *
 * @property {(type: ToastType, title: string, message?: string, durationOrOptions?: number | { duration?: number, actionLabel?: string, onAction?: () => void }) => string} pushToast
 * @property {(id: string) => void} dismissToast
 *
 * @property {(opts: { title: string, message: string, confirmText?: string, type?: ConfirmType, onConfirm: () => void }) => void} openConfirm
 * @property {() => void} closeConfirm
 *
 * @property {(initialQuery?: string) => void} openGlobalSearch
 * @property {() => void} closeGlobalSearch
 */

/** @type {import('zustand').UseBoundStore<import('zustand').StoreApi<UiState>>} */
export const useUiStore = create((set, get) => ({
  activeTab: readActiveTab(),
  sidebarCollapsed: readSidebarCollapsed(),
  sidebarMobileOpen: false,
  toasts: [],
  confirm: null,
  globalSearchOpen: false,
  globalSearchInitialQuery: '',
  adminSettingsOpen: false,
  adminSettingsTab: null,
  /** { hostname, deviceName } when the global Remote Access Tools modal is open. */
  remoteToolsTarget: null,
  theme: effectiveTheme(),

  /** Open Admin Settings, optionally on a specific tab id (e.g. 'infrastructure'). */
  openAdminSettings(tab = null) {
    set({ adminSettingsOpen: true, adminSettingsTab: tab });
  },
  closeAdminSettings() {
    set({ adminSettingsOpen: false, adminSettingsTab: null });
  },

  /** Open the Remote Access Tools modal for a device from anywhere (e.g. search).
   *  @param {{ hostname: string, deviceName?: string }} target */
  openRemoteTools(target) {
    if (target?.hostname) set({ remoteToolsTarget: target });
  },
  closeRemoteTools() {
    set({ remoteToolsTarget: null });
  },

  /** @param {'light' | 'dark'} theme */
  setTheme(theme) {
    applyTheme(theme);
    set({ theme });
  },

  toggleTheme() {
    const next = get().theme === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    set({ theme: next });
  },

  setActiveTab(tabId) {
    if (!VALID_TABS.has(tabId)) {
      console.warn(`Unknown tab id: ${tabId}`);
      return;
    }
    try {
      sessionStorage.setItem(ACTIVE_TAB_STORAGE_KEY, tabId);
    } catch {
      /* ignore */
    }
    set({ activeTab: tabId, sidebarMobileOpen: false });
  },

  toggleSidebarCollapsed() {
    const next = !get().sidebarCollapsed;
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, next ? '1' : '0');
    } catch {
      /* ignore */
    }
    set({ sidebarCollapsed: next });
  },

  setSidebarMobileOpen(value) {
    set({ sidebarMobileOpen: value ?? !get().sidebarMobileOpen });
  },

  /**
   * The 4th argument accepts EITHER a duration in ms (every existing caller) or
   * an options object { duration, actionLabel, onAction }. Kept
   * backward-compatible on purpose: there are ~200 pushToast calls and changing
   * the signature outright would break every one of them silently, since a
   * number in an object position typechecks nowhere in a .js file.
   */
  pushToast(type, title, message, durationOrOptions = 5000) {
    const id = uid();
    const opts = typeof durationOrOptions === 'number' || durationOrOptions == null
      ? { duration: durationOrOptions ?? 5000 }
      : durationOrOptions;
    /** @type {ToastEntry} */
    const toast = {
      id,
      type,
      title,
      message,
      duration: typeof opts.duration === 'number' ? opts.duration : 5000,
      actionLabel: opts.actionLabel,
      onAction: opts.onAction
    };
    set((state) => ({ toasts: [...state.toasts, toast] }));
    return id;
  },

  dismissToast(id) {
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
  },

  openConfirm({ title, message, confirmText = 'Confirm', type = 'warning', onConfirm }) {
    set({
      confirm: {
        title,
        message,
        confirmText,
        type,
        onConfirm
      }
    });
  },

  closeConfirm() {
    set({ confirm: null });
  },

  openGlobalSearch(initialQuery = '') {
    set({ globalSearchOpen: true, globalSearchInitialQuery: initialQuery });
  },

  closeGlobalSearch() {
    set({ globalSearchOpen: false, globalSearchInitialQuery: '' });
  }
}));
